import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconChevronDown,
  IconFlask,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@/lib/icons";
import { Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { LabTestFormDialog } from "@/features/references/LabTestFormDialog";
import {
  type LabTestDefinition,
  deleteLabTestDefinition,
  listTestDefinitions,
} from "@/lib/api/laboratory";
import { useAuthStore } from "@/stores/auth";

const PANEL_COLORS: Record<string, string> = {
  cbc: "red",
  biochem: "orange",
  urinalysis: "yellow",
  serology: "blue",
};

export function LabTestsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [q, setQ] = useState("");
  const [debQ] = useDebouncedValue(q, 300);
  const [dialogOpen, dialogHandlers] = useDisclosure(false);
  const [editing, setEditing] = useState<LabTestDefinition | null>(null);
  const [deleting, setDeleting] = useState<LabTestDefinition | null>(null);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  const onSearchChange = (v: string) => {
    setQ(v);
    setPage(1);
  };
  const onPageSizeChange = (v: string | null) => {
    if (v) {
      setPageSize(Number(v));
      setPage(1);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["lab-test-definitions"],
    queryFn: listTestDefinitions,
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => deleteLabTestDefinition(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-test-definitions"] });
      notifications.show({ color: "green", message: t("references.lab_deleted") });
      setDeleting(null);
    },
  });

  const lang = i18n.language;
  const getName = (item: LabTestDefinition) => {
    if (lang === "uz") return item.name_uz ?? item.name_ru;
    if (lang === "en") return item.name_en ?? item.name_ru;
    return item.name_ru;
  };

  const filtered = (data ?? []).filter(
    (d) =>
      !debQ ||
      d.code.toLowerCase().includes(debQ.toLowerCase()) ||
      getName(d).toLowerCase().includes(debQ.toLowerCase()),
  );
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const formatRange = (low: number | null | undefined, high: number | null | undefined) => {
    if (low === null || low === undefined) return "—";
    return `${low} – ${high ?? ""}`;
  };

  const onAdd = () => {
    setEditing(null);
    dialogHandlers.open();
  };

  const onEdit = (row: LabTestDefinition) => {
    setEditing(row);
    dialogHandlers.open();
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("references.lab_tests")}
        subtitle={`${total}`}
        back
        actions={
          canEdit ? (
            <Button
              leftSection={<IconPlus size={16} strokeWidth={2.4} />}
              onClick={onAdd}
              radius="xl"
            >
              {t("references.lab_create")}
            </Button>
          ) : undefined
        }
      />

      <Card padding="md">
        <Group justify="space-between" wrap="wrap">
          <TextInput
            rightSection={<IconSearch size={18} strokeWidth={2} />}
            placeholder={t("references.lab_tests_search")}
            value={q}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 280 }}
          />
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {t("audit.page_size")}:
            </Text>
            <Select
              size="sm"
              w={92}
              data={["25", "50", "100"]}
              value={String(pageSize)}
              onChange={onPageSizeChange}
            />
          </Group>
        </Group>
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={780}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("references.lab_test_fields.name")}</Table.Th>
                <Table.Th>{t("references.lab_test_fields.unit")}</Table.Th>
                <Table.Th>{t("references.lab_test_fields.panel")}</Table.Th>
                <Table.Th>{t("references.lab_test_fields.male_range")}</Table.Th>
                <Table.Th>{t("references.lab_test_fields.female_range")}</Table.Th>
                {canEdit && <Table.Th style={{ width: 64 }}></Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={canEdit ? 6 : 5}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={canEdit ? 6 : 5}>
                    <EmptyState
                      icon={<IconFlask size={32} strokeWidth={1.5} />}
                      title={t("references.lab_tests_empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {pageItems.map((d) => (
                <Table.Tr key={d.code}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {getName(d)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{d.unit ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    {d.panel ? (
                      <Badge
                        variant="light"
                        color={PANEL_COLORS[d.panel] ?? "blue"}
                        radius="sm"
                      >
                        {t(`references.lab_panels.${d.panel}`, { defaultValue: d.panel })}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">
                      {formatRange(d.normal_low_male, d.normal_high_male)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">
                      {formatRange(d.normal_low_female, d.normal_high_female)}
                    </Text>
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" radius="xl">
                            <IconChevronDown size={16} strokeWidth={2} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<Pencil size={14} strokeWidth={2.2} />}
                            onClick={() => onEdit(d)}
                          >
                            {t("common.edit")}
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} strokeWidth={2.2} />}
                            color="red"
                            onClick={() => setDeleting(d)}
                          >
                            {t("common.delete")}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {totalPages > 1 && (
        <Group justify="center" mt="sm">
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            radius="md"
            size="sm"
          />
        </Group>
      )}

      <LabTestFormDialog
        opened={dialogOpen}
        onClose={() => {
          dialogHandlers.close();
          setEditing(null);
        }}
        initial={editing}
      />

      <Modal
        opened={!!deleting}
        onClose={() => setDeleting(null)}
        title={t("references.lab_delete_confirm_title")}
        size="sm"
        radius="lg"
      >
        <Stack>
          <Text size="sm">
            {t("references.lab_delete_confirm", { code: deleting?.code ?? "" })}
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setDeleting(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleting && deleteMutation.mutate(deleting.code)}
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
